import React, { useCallback, useContext, useMemo } from "react";
import styled from "styled-components";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import {
  useClientsData,
  useExchangesMetadata,
} from "@shared/utils/hooks/misc.ts";
import { EXCHANGES } from "@shared/common/constants.ts";
import { isSymbol } from "@shared/types/utils.ts";
import { openMessage } from "utils/misc.tsx";
import { ClientData, ExchangeTag } from "@shared/types";
import ExchangesTagDropdown from "features/bots/BotForm/General/ExchangesTagDropdown.tsx";
import { Tag } from "features/bots/BotForm/common/styles.ts";
import { getBotFormExchangeTagId } from "testIds.ts";

interface ExchangesTagsProps {
  getAccountErrorMessage?: (account: ClientData) => string | undefined;
  getExchangeErrorMessage?: (exchange: string) => string | undefined;
}

const ExchangesTags = ({
  getAccountErrorMessage,
  getExchangeErrorMessage,
}: ExchangesTagsProps) => {
  const { setAccounts, accounts } = useContext(BotFormContext)!;
  const { getExchangeTag } = useExchangesMetadata();
  const { activeClientsData, getAccountById } = useClientsData();

  const selectedAccountIds = useMemo(
    () =>
      isSymbol(accounts)
        ? undefined
        : new Set(accounts?.map((account) => account.client_id)),
    [accounts]
  );

  const availableClients = useMemo(
    () =>
      activeClientsData?.filter(
        (client) => client.exchange !== EXCHANGES.BITMEX
      ),
    [activeClientsData]
  );

  const getClientErrorMessage = useCallback(
    (client: ClientData) => {
      return (
        getAccountErrorMessage?.(client) ??
        getExchangeErrorMessage?.(client.exchange)
      );
    },
    [getAccountErrorMessage, getExchangeErrorMessage]
  );

  const selectableClients = useMemo(
    () =>
      availableClients?.filter((client) => !getClientErrorMessage(client)),
    [availableClients, getClientErrorMessage]
  );

  const tagToAccountIds = useMemo(() => {
    return Object.values(ExchangeTag).reduce((acc, tag) => {
      acc[tag] =
        selectableClients
          ?.filter((client) => {
            return getExchangeTag(client.exchange) === tag;
          })
          .map((client) => client.client_id) || [];
      return acc;
    }, {} as Record<ExchangeTag, number[]>);
  }, [selectableClients, getExchangeTag]);

  const getTagErrorMessage = (exchangeTag: ExchangeTag) => {
    const invalidClient = availableClients?.find((client) => {
      return getExchangeTag(client.exchange) === exchangeTag;
    });
    return invalidClient ? getClientErrorMessage(invalidClient) : undefined;
  };

  const getTagSelected = (exchangeTag: ExchangeTag) => {
    const accountIds = tagToAccountIds[exchangeTag];
    return (
      !!accountIds.length &&
      accountIds.every((accountId) => selectedAccountIds?.has(accountId))
    );
  };

  const onTagClick = (exchangeTag: ExchangeTag) => {
    const accountIds = tagToAccountIds[exchangeTag];
    if (!accountIds.length) {
      openMessage(
        getTagErrorMessage(exchangeTag) ??
          `You don't have any ${exchangeTag.toLowerCase()} accounts.`,
        "warning"
      );
    } else if (getTagSelected(exchangeTag)) {
      setAccounts([]);
    } else {
      const nextAccounts = accountIds
        .map(getAccountById)
        .filter((account) => !!account);
      setAccounts(nextAccounts);
    }
  };

  return (
    <TagsWrapper>
      {Object.values(ExchangeTag).map((exchangeTag) => {
        const isInvalid = !tagToAccountIds[exchangeTag].length;
        return (
          <Tag
            key={exchangeTag}
            data-testid={getBotFormExchangeTagId(exchangeTag)}
            $selected={getTagSelected(exchangeTag)}
            $isInvalid={isInvalid}
            onClick={() => onTagClick(exchangeTag)}
          >
            {exchangeTag}
          </Tag>
        );
      })}
      <ExchangesTagDropdown
        getClientErrorMessage={getClientErrorMessage}
        selectedAccountIds={selectedAccountIds}
        validClients={availableClients}
      />
    </TagsWrapper>
  );
};

export default ExchangesTags;

const TagsWrapper = styled.div`
  margin-top: 6px;
  display: flex;
  column-gap: 6px;
  align-items: center;
  position: relative;
`;
